import {ActionReducerMapBuilder} from '@reduxjs/toolkit';
import {actions} from '../actions';
import {TimeslotsState} from '../stateTypes';
import {getPendingKeysOwnedByRequest} from '../utils';
import {dateKeyForDateTimeString, datesForRange} from '../dateTimeHelpers';
import {groupBy} from '@lib/utils';
import {registerMutation, reservationCompare, resolveMutation} from './mutationUtils';
import {Workday} from '../apiTypes';

export const extendWithWorkdayReducers = (builder: ActionReducerMapBuilder<TimeslotsState>) => {
  // Workdays
  builder
    .addCase(actions.fetchWorkdays.pending, (state, action) => {
      // Mark each day in the range as pending
      for (const dateKey of datesForRange(action.meta.arg)) {
        state.workdaysByDate[dateKey] = {
          status: 'pending',
          data: state.workdaysByDate[dateKey]?.data ?? null,
          requestId: action.meta.requestId,
        };
      }
    })
    .addCase(actions.fetchWorkdays.fulfilled, (state, action) => {
      const workdaysByDate = groupBy(action.payload, (wd) => dateKeyForDateTimeString(wd.localStart));
      const pendingForThisRequest = getPendingKeysOwnedByRequest(
        datesForRange(action.meta.arg),
        state.workdaysByDate,
        action.meta.requestId,
      );

      // Fulfill each day in range, if request still owns the pending state
      for (const dateKey of pendingForThisRequest) {
        state.workdaysByDate[dateKey] = {
          status: 'fulfilled',
          data: workdaysByDate[dateKey]?.sort(reservationCompare) ?? [],
        };
      }
    })
    .addCase(actions.fetchWorkdays.rejected, (state, action) => {
      const pendingForThisRequest = getPendingKeysOwnedByRequest(
        datesForRange(action.meta.arg),
        state.workdaysByDate,
        action.meta.requestId,
      );
      // Reject each day in range, if request still owns the pending state
      for (const dateKey of pendingForThisRequest) {
        state.workdaysByDate[dateKey] = {
          status: 'rejected',
          data: null,
          errorMessage:
            action.error.message ??
            `Something went wrong loading the workdays between ${action.meta.arg.to} and ${action.meta.arg.from}`,
        };
      }
    })

    // Creation
    .addCase(actions.createWorkday.pending, (state, action) => {
      registerMutation(state, {
        type: 'create',
        data: action.meta.arg,
        requestId: action.meta.requestId,
      });
    })
    .addCase(actions.createWorkday.fulfilled, (state, action) => {
      // Inject workday
      addWorkday(state, action.payload);
      resolveMutation(state, 'create', action.meta.requestId);
    })
    .addCase(actions.createWorkday.rejected, (state, action) => {
      resolveMutation(state, 'create', action.meta.requestId);
    });

  // Deletion
  builder
    .addCase(actions.cancelWorkday.pending, (state, action) => {
      registerMutation(state, {
        type: 'delete',
        data: action.meta.arg,
        requestId: action.meta.requestId,
      });
    })
    .addCase(actions.cancelWorkday.fulfilled, (state, action) => {
      const canceledWorkday = action.meta.arg;
      for (const [date, entry] of Object.entries(state.workdaysByDate)) {
        if (entry?.status === 'fulfilled') {
          if (entry.data.some((r) => r.id === canceledWorkday.id)) {
            entry.data = entry.data.filter((r) => r.id !== canceledWorkday.id);
          }
        }
      }
      resolveMutation(state, 'delete', action.meta.requestId);
    })
    .addCase(actions.cancelWorkday.rejected, (state, action) => {
      resolveMutation(state, 'delete', action.meta.requestId);
    });

  // Modifications
  builder
    .addCase(actions.updateWorkday.pending, (state, action) => {
      registerMutation(state, {
        type: 'update',
        data: action.meta.arg,
        requestId: action.meta.requestId,
      });
    })
    .addCase(actions.updateWorkday.fulfilled, (state, action) => {
      const workdayId = action.meta.arg.id;
      const updatedWorkday = action.payload;

      removeWorkday(state, workdayId);
      addWorkday(state, updatedWorkday);
      resolveMutation(state, 'update', action.meta.requestId);
    })
    .addCase(actions.updateWorkday.rejected, (state, action) => {
      resolveMutation(state, 'update', action.meta.requestId);
    });
};

const removeWorkday = (state: TimeslotsState, workdayId: string) => {
  for (const [date, entry] of Object.entries(state.workdaysByDate)) {
    if (entry?.status === 'fulfilled') {
      // This check makes the removal slightly more expensive but has the benefit of not recreating every array
      if (entry.data.some((r) => r.id === workdayId)) {
        entry.data = entry.data.filter((r) => r.id !== workdayId);
      }
    }
  }
};

const addWorkday = (state: TimeslotsState, workday: Workday) => {
  const dateKey = dateKeyForDateTimeString(workday.localStart);
  const entry = state.workdaysByDate[dateKey];
  if (!entry) {
    throw new Error('Workday cannot be added to a day without loading existing data first');
  }

  if (entry.status !== 'fulfilled') {
    console.warn('Workday was added to day that is loading / failed to load');
  }

  const existing = entry.data ?? [];
  state.workdaysByDate[dateKey] = {
    status: 'fulfilled',
    data: existing.concat(workday).sort(reservationCompare),
  };
};
